/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { ActivityObject } from '../../../params/redux/activity';
import ApexCharts from 'apexcharts';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import { badgeColors } from '../../../helper';
import { brand } from '../../../theme/themePrimitives';
import CardWrapper from '../../../components/wrapper/card';
import CustomDropdown from '../../../components/reusable/dropdown';
import { fetchAllActivitiesThunk } from '../../../redux/thunks/activity';
import { fetchHotLeadsThunk } from '../../../redux/thunks/lead';
import { fetchRecentMeetingsThunk } from '../../../redux/thunks/calendar';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import LabelWrapper from '../../../components/wrapper/label';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import ReactApexChart from 'react-apexcharts';
import RecentLeads from './recent_leads';
import RecentMeetings from './recent_meetings';
import SupervisedUserCircleOutlinedIcon from '@mui/icons-material/SupervisedUserCircleOutlined';
import { VerifiedUser } from '@mui/icons-material';
import { AppDispatch, RootState } from '../../../redux/store';
import { Avatar, Box, CircularProgress, Grid, SelectChangeEvent, Typography } from '@mui/material';
import { fetchDealsBreakDownForSalesThunk, fetchSalesAnalyticsDataThunk } from '../../../redux/thunks/chart';
import { getAllPipelinesThunk, getSinglePipelineDetailThunk } from '../../../redux/thunks/pipeline';
import { processGraphData, SalesAnalytics } from './process_graph_data';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useRef, useState } from 'react';

type TimeType = 'All time' | 'Last 6 months' | 'Last 3 months' | 'Last month' | 'Last week';

const dateData = [
    { label: 'Last week', value: 'Last week' },
    { label: 'Last Month', value: 'Last month' },
    { label: 'Last 3 Months', value: 'Last 3 months' },
    { label: 'Last 6 Months', value: 'Last 6 months' },
    { label: 'All Time', value: 'All time' }
];

const LoaderWrapper = ({ height }: { height: string | number }) => {
    return <Box display="flex" flexDirection="column" gap={2} flex={1} justifyContent="center" alignItems="center" height={height}>
        <CircularProgress />
        <Typography>Loading...</Typography>
    </Box>;
};

const SalesDashboard = () => {
    // const { dispatchCancelable } = useCancelableThunk();
    const dispatch = useDispatch<AppDispatch>();
    const chartRef = useRef<HTMLDivElement>(null);
    const [selectedTime, setSelectedTime] = useState<TimeType>('Last week');
    const { currentUser } = useSelector((state: RootState) => state.app);
    const { activities } = useSelector((state: RootState) => state.activity);
    const { leads } = useSelector((state: RootState) => state.lead);
    const { recentMeetings } = useSelector((state: RootState) => state.calendar);
    const { sales_dashboard, loaders } = useSelector((state: RootState) => state.chart);
    const { pipelines, selectedPipeline, loaders: pipelineLoaders } = useSelector((state: RootState) => state.pipeline);
    const { months, totalDeals, totalRevenue, customersAcquired } = processGraphData(sales_dashboard.salesAnalytics as SalesAnalytics || {} as SalesAnalytics);

    const donustOptions: ApexCharts.ApexOptions = useMemo(() => ({
        chart: { type: 'donut', height: 180 },
        labels: sales_dashboard.donutCategories,
        dataLabels: { enabled: false },
        legend: { fontSize: '10px', itemMargin: { horizontal: 5, vertical: 3 }},
        responsive: [{ breakpoint: 480, options: { chart: { height: 200 }, legend: { position: 'bottom' }}}]
    }), [sales_dashboard.donutCategories]);

    const chartOptions: ApexCharts.ApexOptions = {
        chart: { type: 'line', height: 400, zoom: { enabled: false }},
        xaxis: { categories: months },
        stroke: { curve: 'smooth' }
    };

    const chartSeries: ApexAxisChartSeries = [
        { name: 'Total Deals', data: totalDeals },
        { name: 'Total Revenue', data: totalRevenue },
        { name: 'Customers Acquired', data: customersAcquired }
    ];

    const statisticsDetail = [
        {
            label: 'Total Deals',
            total: sales_dashboard.total_deals,
            percentage: '0.0%',
            icon: <SupervisedUserCircleOutlinedIcon color="success" />,
            color: 'green'
        },
        {
            label: 'Total Revenue',
            total: `$ ${parseFloat(sales_dashboard.total_revenue).toFixed(2)}`,
            percentage: '0.0%',
            icon: <AttachMoneyOutlinedIcon color="primary" />,
            color: 'blue'
        },
        {
            label: 'Customers Acquired',
            total: sales_dashboard.customer_acquired,
            percentage: '0.0%',
            icon: <PersonOutlineOutlinedIcon color="warning" />,
            color: 'yellow'
        },
        {
            label: 'Conversion Rate',
            total: `${sales_dashboard.conversion_rate}%`,
            percentage: '',
            icon: <InsertChartOutlinedIcon color="error" />,
            color: 'red'
        }
    ];
    useEffect(() => {
        const fetchAllActivities = async () => {
            try {
                await Promise.all([
                    dispatch(fetchAllActivitiesThunk()).unwrap(),
                    dispatch(getAllPipelinesThunk(0)).unwrap(),
                    dispatch(fetchRecentMeetingsThunk()).unwrap(),
                    dispatch(fetchHotLeadsThunk()).unwrap(),
                    dispatch(fetchSalesAnalyticsDataThunk(selectedTime)).unwrap(),
                    dispatch(fetchDealsBreakDownForSalesThunk(selectedTime)).unwrap()
                ]);
                // dispatchCancelable(fetchAllActivitiesThunk, {});
                // dispatchCancelable(getAllPipelinesThunk, {});
                // dispatchCancelable(fetchSalesAnalyticsDataThunk, { selectedTime });
                // dispatchCancelable(fetchDealsBreakDownForSalesThunk, { selectedTime });
                // await dispatch(getAllPipelinesThunk()).unwrap();
                // await dispatch(fetchSalesAnalyticsDataThunk(selectedTime)).unwrap();
                // await dispatch(fetchDealsBreakDownForSalesThunk(selectedTime)).unwrap();
            } catch (e) {
                // Catch errors
            }
        };
        fetchAllActivities();
    }, []);

    const handlePipelineChange = async (event: SelectChangeEvent<unknown>) => {
        event.preventDefault();
        const pipelineId = parseInt(event.target.value as string, 10);
        try {
            await dispatch(getSinglePipelineDetailThunk(pipelineId)).unwrap();
        } catch (e) {
            //
        }
    };

    const timeChangeHandler = async (event: SelectChangeEvent<unknown>) => {
        event.preventDefault();
        const target = event.target.value;
        try {
            await dispatch(fetchSalesAnalyticsDataThunk(target as TimeType)).unwrap();
            await dispatch(fetchDealsBreakDownForSalesThunk(target as TimeType)).unwrap();
            setSelectedTime(target as TimeType);
        } catch (e) {
            //
        }
    };

    return <Box padding="20px 10px" minHeight="80vh" overflow="auto">
        <CardWrapper>
            <Typography variant="h6" mb={2} fontWeight="bold"> Welcome {currentUser?.name || `${currentUser?.first_name} ${currentUser?.last_name}`}</Typography>
            <CardWrapper ref={chartRef}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" mb={2} fontWeight="bold"> Statistics </Typography>
                    <Box><CustomDropdown
                        options={dateData}
                        onChange={timeChangeHandler}
                        error={false}
                        value={selectedTime}
                        disabled={loaders.isLoading}
                    /></Box>
                </Box>
                <Box display="flex" alignItems="start" justifyContent="space-between">
                    {
                        statisticsDetail.map((stat, index) => {
                            return <Box display="flex" gap="10px" key={index}>
                                <Box height="40px" width="40px" display="flex" borderRadius="10px" alignItems="center" justifyContent="center" sx={{
                                    backgroundColor: stat.color === 'yellow' ? 'rgba(255, 204, 0, 4%)' : stat.color === 'blue' ? 'rgba(0, 0, 255, 4%)' : stat.color === 'red' ? 'rgba(255, 0, 0, 4%)' : 'rgba(0, 255, 0, 4%)'
                                }}>{stat.icon}</Box>
                                <Box>
                                    <Typography>{stat.label}</Typography>
                                    <Typography><b>{stat.total}</b> {stat.percentage && `(${stat.percentage})`}</Typography>
                                </Box>
                            </Box>;
                        })
                    }
                </Box>
                {loaders.isLoading
                    ? <LoaderWrapper height={500} />
                    : <ReactApexChart options={chartOptions} series={chartSeries} type="line" height={500} />}
            </CardWrapper>
            <Box display="flex" gap="20px" marginTop="20px" mb={2}>
                <CardWrapper>
                    <Typography variant="h6" mb={2} fontWeight="bold">Activity Feed</Typography>
                    <Box maxHeight="300px" minHeight="300px" overflow="auto">
                        {activities.map((activity: ActivityObject) => (
                            <Box key={activity.id} display="flex" alignItems="center" mb={2}>
                                {/* Icon */}
                                <Avatar
                                    sx={{
                                        bgcolor: badgeColors[activity.badge_color],
                                        color: '#FFF',
                                        width: 40,
                                        height: 40,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        marginRight: 2
                                    }}
                                >
                                    <VerifiedUser sx={{ color: 'white' }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="body1" fontWeight="bold">{activity.description}</Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {new Date(activity.created_at).toLocaleString('en-US', {
                                            weekday: 'long',
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </CardWrapper>
                <CardWrapper>
                    <Typography variant="h6" fontWeight="bold">Deals Breakdown</Typography>
                    <Typography variant="body2" color="textSecondary" mb={1}>By Niche</Typography>
                    {loaders.isLoading
                        ? <LoaderWrapper height="300px" />
                        : <Box maxHeight="300px" minHeight="300px" overflow="auto">
                            {sales_dashboard.dealBreakDown.map((deal, index) => (
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    padding="8px 0"
                                    key={index}>
                                    <Typography variant="body1" color="textSecondary" fontWeight={400}>{deal.niche}</Typography>
                                    <Box display="flex" alignItems="center">
                                        <Typography variant="body1" color="textSecondary" mr={2}>{deal.percentage.toFixed(2)} %</Typography>
                                        <Box
                                            position="relative"
                                            display="inline-flex"
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <CircularProgress
                                                variant="determinate"
                                                value={deal.percentage}
                                                size={40}
                                                thickness={4}
                                                sx={{ color: brand[300], zIndex: '100' }}
                                            />
                                            <CircularProgress
                                                variant="determinate"
                                                value={100}
                                                size={40}
                                                thickness={4}
                                                sx={{ position: 'absolute', color: '#E0E0E0' }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>}
                </CardWrapper>
            </Box>
            {/* Add a Recent Meetings and leads */}
            <CardWrapper sx={{ py: 4, my: 4 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <RecentMeetings meetings={recentMeetings} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <RecentLeads leads={leads} />
                    </Grid>
                </Grid>
            </CardWrapper>
            <CardWrapper>
                {pipelineLoaders.isLoading
                    ? <LoaderWrapper height="400px" />
                    : <Box display="flex" justifyContent="space-between" alignItems="center" minHeight="400px">
                        <Box flex={1}>
                            <Box width="100%" boxSizing="border-box">
                                <LabelWrapper label="Pipeline" />
                                <CustomDropdown
                                    options={pipelines.map((pipe) => ({
                                        label: pipe.name,
                                        value: pipe.id
                                    }))}
                                    onChange={handlePipelineChange}
                                    error={false}
                                    placeholder="Select Pipeline"
                                    value={selectedPipeline ? selectedPipeline.id : ''}
                                />
                            </Box>
                            <Typography variant="h4" align="center" mt={2} mb={1}>$0 Total Value</Typography>
                            <Typography variant="subtitle1" align="center">Total Deals - {selectedPipeline?.pipeline_stages?.reduce((a, b) => a + (b.deals.length ?? 0), 0) || 0}</Typography>
                        </Box>
                        <Box flex={2}></Box>
                        <Box flex={2}>
                            <Typography variant="h5" align="center" gutterBottom>Stage Distribution</Typography>
                            {selectedPipeline?.pipeline_stages?.length === 0
                                ? <Box>
                                    <Typography variant="subtitle1" align="center" color="textDisabled" my={5}>No stage found for selected pipeline</Typography>
                                </Box>
                                : <Box >
                                    <ReactApexChart options={donustOptions} series={sales_dashboard.donutChartSeries} type="donut" height={200} width={420} />
                                </Box>}
                        </Box>
                        <Box flex={2}></Box>
                    </Box>}
            </CardWrapper>
        </CardWrapper>
    </Box>;
};
export default SalesDashboard;